import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmailHistory } from "./ComposeSchoolEmail";

const defaultMsg = (name) => `Our records show that your ${name} registration is still awaiting payment.\n\nPlease log in to your Adventure Rock dashboard and open Bookings to see the amount due and payment details. Your Explorer's spot is held for you, so please complete payment as soon as possible.\n\nIf you've already paid, thank you, and please ignore this message. Questions? Just reply to this email.`;

function RemindUnpaidDialog({ open, count, campId, campName, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSubject(`Payment reminder: ${campName}`); setBody(defaultMsg(campName)); } }, [open, campName]);
  const send = async () => {
    if (!window.confirm(`Send this payment reminder to ${count} unpaid famil${count === 1 ? "y" : "ies"}?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/camp-emails/remind", { camp_id: campId, subject, body }); toast.success(`Sending reminder to ${data.recipients} families`); onClose(true); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-lg" data-testid="camp-remind-dialog">
        <DialogHeader><DialogTitle className="font-display text-navy">Payment reminder</DialogTitle>
          <DialogDescription>{count} unpaid famil{count === 1 ? "y" : "ies"} · each gets a private email starting “Hi [name],” plus an in-app message. Opens are tracked.</DialogDescription></DialogHeader>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl" data-testid="camp-remind-subject" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="rounded-xl min-h-[200px]" data-testid="camp-remind-body" />
        <Button onClick={send} disabled={busy || !subject.trim() || !body.trim()} className="w-full rounded-full bg-coral text-white font-bold h-11" data-testid="camp-remind-send"><Send className="w-4 h-4 mr-1" /> Send to {count}</Button>
      </DialogContent>
    </Dialog>
  );
}

const GROUPS = [["all", "All registered"], ["paid", "Paid"], ["unpaid", "Not paid"]];

export function CampEmailCopy({ camps }) {
  const [campId, setCampId] = useState("all");
  const [data, setData] = useState(null);
  const [show, setShow] = useState(null);
  const [remind, setRemind] = useState(false);
  const [tick, setTick] = useState(0);
  const months = [...new Set(camps.flatMap((c) => c.dates || []).map((d) => d.slice(0, 7)))].sort();
  const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en", { month: "long", year: "numeric" });
  const campName = campId === "all" ? "camp" : campId.startsWith("month:") ? `${monthLabel(campId.slice(6))} camp` : camps.find((c) => c.id === campId)?.name || "camp";
  const unpaid = data?.unpaid?.length || 0;
  useEffect(() => { api.get("/admin/camp-emails", { params: { camp_id: campId } }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, [campId, camps.length]);
  const copy = async (key) => {
    const list = data?.[key] || [];
    if (!list.length) return toast.error("No emails in this group");
    const emails = list.map((f) => f.email).join(", ");
    try { await navigator.clipboard.writeText(emails); toast.success(`${list.length} emails copied — paste into BCC`); }
    catch { window.prompt("Copy these emails:", emails); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="camp-email-copy">
      <div className="flex flex-wrap items-center gap-2">
        <Mail className="w-5 h-5 text-turquoise" />
        <p className="font-display font-bold text-navy flex-1">Camp parent emails</p>
        <select value={campId} onChange={(e) => setCampId(e.target.value)} className="h-9 rounded-xl border border-slate-200 px-2 text-sm bg-white" data-testid="camp-email-camp-select">
          <option value="all">All camps</option>
          {months.length > 0 && <optgroup label="By month">{months.map((m) => <option key={m} value={`month:${m}`}>{monthLabel(m)} camps only</option>)}</optgroup>}
          <optgroup label="Single camp">{camps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
        </select>
      </div>
      {data && (
        <p className="text-sm text-navy" data-testid="camp-email-counts">
          <b>{data.explorer_count}</b> Explorer{data.explorer_count === 1 ? "" : "s"} enrolled · <b>{data.all.length}</b> parent email{data.all.length === 1 ? "" : "s"}
          <span className="text-slate-500 text-xs"> — siblings share one parent email, so this can be lower than the roster</span>
        </p>
      )}
      {data?.no_email?.length > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800" data-testid="camp-no-email">
          <b>{data.no_email.length} Explorer{data.no_email.length === 1 ? "" : "s"} with no parent email on file:</b> {data.no_email.map((n) => `${n.explorer} (${n.parent_name}${n.phone ? ` · ${n.phone}` : ""})`).join(", ")}
        </div>
      )}
      <p className="text-xs text-slate-500">A family counts as "Not paid" if any of their camp registrations is still awaiting payment.</p>
      <div className="flex flex-wrap gap-2">
        {GROUPS.map(([k, label]) => (
          <div key={k} className="flex items-center gap-1">
            <Button size="sm" className={`rounded-full font-bold ${k === "unpaid" ? "bg-coral text-white" : k === "paid" ? "bg-turquoise text-navy" : "bg-navy text-white"}`} onClick={() => copy(k)} data-testid={`camp-copy-${k}`}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy {label} ({data?.[k]?.length ?? 0})
            </Button>
            <button className="text-xs text-slate-500 underline" onClick={() => setShow(show === k ? null : k)} data-testid={`camp-show-${k}`}>{show === k ? "hide" : "view"}</button>
          </div>
        ))}
      </div>
      {show && <div className="divide-y divide-slate-100 text-sm" data-testid={`camp-list-${show}`}>
        {(data?.[show] || []).map((f) => (
          <p key={f.email} className="py-1.5 flex flex-wrap justify-between gap-2"><span><b className="text-navy">{f.parent_name}</b> <span className="text-slate-400">· {f.explorers.join(", ")}</span></span>
            <span className="text-slate-500">{f.email}{f.phone ? ` · ${f.phone}` : ""}</span></p>))}
        {!data?.[show]?.length && <p className="text-slate-400 py-1.5">Nobody in this group.</p>}
      </div>}
      <Button onClick={() => setRemind(true)} disabled={!unpaid} className="w-full sm:w-auto rounded-full bg-coral text-white font-bold" data-testid="camp-remind-btn"><Send className="w-4 h-4 mr-1" /> Email payment reminder to {unpaid} unpaid</Button>
      <EmailHistory refresh={tick} />
      <RemindUnpaidDialog open={remind} count={unpaid} campId={campId} campName={campName} onClose={(sent) => { setRemind(false); if (sent) setTick((t) => t + 1); }} />
    </div>
  );
}
